// Interface flags per tenant. The redesign (Fase 9 de maic Learn) ships behind
// `ui_v2`: the grouped sidebar, Home as the landing page, the help menu and the
// single Cmd+K search. Off by default, so a tenant keeps today's interface
// until a platform operator turns it on (Plataforma > Tenants, or
// PATCH /admin/tenants/<id>/ui-flags in admin-service).
//
// A per-browser override exists for previews and demos: `?ui=v2` or `?ui=v1`
// in the URL stores it, `?ui=tenant` clears it. It only changes what this
// browser draws; permissions and data are the same either way.
import { create } from 'zustand';
import { authedHeaders } from '../store/authStore';
import type { TenantBillingNotice } from '../api/billingNotice';

const ADMIN_API = import.meta.env.VITE_ADMIN_SERVICE_URL || 'http://localhost:8022';
export const UI_OVERRIDE_KEY = 'nexus.ui_override';

type Override = 'v1' | 'v2' | null;

interface UiFlagsState {
  /** The tenant's flag as admin-service reports it. */
  tenantUiV2: boolean;
  /** This browser's override, if any. */
  override: Override;
  /** Tenant the flag was read for; null until the first answer. */
  loadedFor: string | null;
  /** The tenant's overdue-payment notice, set by a platform operator. It
   *  rides on the same answer; null when off or not yet known. Not cached:
   *  a notice that was cleared must not come back from an old answer. */
  billingNotice: TenantBillingNotice | null;
  load: (tenantId: string) => Promise<void>;
  setOverride: (value: Override) => void;
}

function readOverride(): Override {
  try {
    const v = localStorage.getItem(UI_OVERRIDE_KEY);
    return v === 'v1' || v === 'v2' ? v : null;
  } catch {
    return null;
  }
}

function writeOverride(value: Override) {
  try {
    if (value) localStorage.setItem(UI_OVERRIDE_KEY, value);
    else localStorage.removeItem(UI_OVERRIDE_KEY);
  } catch { /* storage blocked: the override lasts this page load */ }
}

/** `?ui=v2|v1|tenant` in the address bar sets or clears the override. */
export function applyUiOverrideFromUrl(search: string = typeof window !== 'undefined' ? window.location.search : ''): Override {
  const v = new URLSearchParams(search).get('ui');
  if (v === 'v1' || v === 'v2') writeOverride(v);
  else if (v === 'tenant') writeOverride(null);
  return readOverride();
}

// Last answer per tenant, so a reload draws the right interface at once
// instead of flashing the old one while admin-service answers.
const cacheKey = (tenantId: string) => `nexus.ui_v2.${tenantId}`;

function readCached(tenantId: string): boolean {
  try { return localStorage.getItem(cacheKey(tenantId)) === '1'; } catch { return false; }
}

function writeCached(tenantId: string, on: boolean) {
  try {
    if (on) localStorage.setItem(cacheKey(tenantId), '1');
    else localStorage.removeItem(cacheKey(tenantId));
  } catch { /* storage blocked */ }
}

export const useUiFlagsStore = create<UiFlagsState>((set, get) => ({
  tenantUiV2: false,
  override: applyUiOverrideFromUrl(),
  loadedFor: null,
  billingNotice: null,
  load: async (tenantId) => {
    if (get().loadedFor === tenantId) return;
    set({ tenantUiV2: readCached(tenantId), loadedFor: tenantId, billingNotice: null });
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    try {
      const r = await fetch(`${ADMIN_API}/admin/me/ui-flags`, { headers: authedHeaders(), signal: ctrl.signal });
      // Only a real answer changes the flag; a failed call keeps the cached one.
      if (!r.ok) return;
      const body = await r.json();
      const on = body?.ui_v2 === true;
      writeCached(tenantId, on);
      if (get().loadedFor === tenantId) set({ tenantUiV2: on, billingNotice: parseBillingNotice(body?.billing_notice) });
    } catch {
      // admin-service down or unreachable: keep what we had.
    } finally {
      clearTimeout(timer);
    }
  },
  setOverride: (value) => {
    writeOverride(value);
    set({ override: value });
  },
}));

/** Only a notice with a known level is shown; anything else is no notice. */
export function parseBillingNotice(raw: unknown): TenantBillingNotice | null {
  if (!raw || typeof raw !== 'object') return null;
  const n = raw as Record<string, unknown>;
  if (n.level !== 'reminder' && n.level !== 'final') return null;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  return {
    level: n.level,
    deadline: str(n.deadline),
    note: str(n.note),
    tenant_name: str(n.tenant_name),
    set_at: str(n.set_at),
  };
}

export function isUiV2(s: Pick<UiFlagsState, 'tenantUiV2' | 'override'>): boolean {
  if (s.override === 'v2') return true;
  if (s.override === 'v1') return false;
  return s.tenantUiV2;
}

/** True when this tenant (or this browser's override) uses the new interface. */
export function useUiV2(): boolean {
  return useUiFlagsStore(isUiV2);
}
